import { useState, useCallback, useEffect, useRef } from "react";
import { isDomesticOfficialSeedance } from '../../utils/domesticSeedanceCapabilities';
import { useToast } from "../../context/ToastContext";
import { useStyle } from "../../context/StyleContext";
import { API_HOST } from "../../utils/constants";
import { authenticatedFetch } from "../../utils/api";
import { extractTaskResultUrl, startTaskPolling, submitTask } from "../../services/taskService";
import {
  isDurableVideoPollingProvider,
  submitVideoTask,
  VIDEO_POLLING_MAX_MS,
} from "../../services/videoService";
import { getNodeImageUrl } from "./canvasUtils";
import { applyGuaVoiceBindingPrompt } from "../../utils/guaVoiceBindings";
import {
  getProjectDefaultVideoRatio,
  getProjectVideoProvider,
  isOverseasVideoProvider,
  isSeedanceVideoProvider,
  normalizeVideoProvider,
  normalizeVideoResolutionForProvider,
} from "../../utils/projectRuntime";
import {
  clearVideoAssetBinding,
  getEffectiveAssetMeta,
} from "../../utils/guaVideoAssetStatus";
import { preflightVideoAssetContents } from "../../utils/videoAssetPreflight";
import { getDreaminaProxyOriginalUrl, isDreaminaProxySeedance20Provider, requireDreaminaProxyOriginalUrl } from "../../utils/dreaminaProxyReferences";
import {
  backfillGenerationHistoryEntry,
  buildGenerationHistoryEntry,
  createSuccessfulGenerationPatch,
} from "./utils/generationHistory";
import { getNodeOutputType } from "./utils/connectionRules";
import { buildCanvasSeedance25Submission } from "./utils/seedance25Submission";
import { getSeedance25ReferenceUrl, isSeedance25Provider, normalizeSeedance25SubmissionProvider } from "../../utils/seedance25Capabilities";
import { normalizeImageProvider } from "../../constants/globalAiProviders";
import {
  assertWanxImageInputLimit,
  getCurrentImageProvider,
  normalizeWanxImageQualityTier,
} from "../../utils/wanxImageCapabilities";
import {
  assertWanxVideoMediaSelection,
  isWanxVideoProvider,
  normalizeWanxVideoDuration,
  normalizeWanxVideoMedia,
  normalizeWanxVideoModel,
  normalizeWanxVideoRatio,
  normalizeWanxVideoResolution,
  normalizeWanxVideoSeed,
  WANX_VIDEO_MEDIA_TYPES,
} from "../../utils/wanxVideoCapabilities";

/**
 * 画布节点生成逻辑 hook
 *
 * P2-1（就地更新模型）：生成器身份不突变 —— generate 节点始终保留 generateType/prompt/全部参数，
 * 成功后仅回写 lastResultUrl/lastGeneratedAt/generatingStatus/resourceId 就地显示结果，
 * 不再自动产出素材节点；需要独立素材节点时由面板/节点上的“导出为素材节点”按钮
 * 显式调用 NodeCanvas.spawnGeneratedResource（带幂等查重）。
 */
const useCanvasGeneration = ({ episodeId }) => {
  const toast = useToast();
  const { currentStyle } = useStyle();
  const [isGenerating, setIsGenerating] = useState(false);
  // 防重触发：在飞生成按节点 id 登记（比 data.isGenerating 更快生效，覆盖双击穿透的同一帧窗口）
  const inflightNodeIds = useRef(new Set());
  const pollingStopsRef = useRef(new Map());

  const releaseInflightNode = useCallback((nodeId) => {
    inflightNodeIds.current.delete(String(nodeId));
    setIsGenerating(inflightNodeIds.current.size > 0);
  }, []);

  const clearPollingStop = useCallback((pollKey, shouldStop = false) => {
    const stop = pollingStopsRef.current.get(pollKey);
    if (shouldStop) stop?.();
    pollingStopsRef.current.delete(pollKey);
  }, []);

  useEffect(() => () => {
    pollingStopsRef.current.forEach((stop) => stop?.());
    pollingStopsRef.current.clear();
    inflightNodeIds.current.clear();
  }, []);

  /**
   * P3-1：生成结果自动写入资源中心（失败 console.warn + toast.warning，不阻塞就地更新）
   * 返回 resourceId 或 null
   */
  const saveGeneratedToResourceCenter = useCallback(async ({ url, resourceType, name, projectId }) => {
    try {
      const saved = await authenticatedFetch(`${API_HOST}/api/resource/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url,
          projectId: projectId || localStorage.getItem('currentProjectId'),
          episodeId: episodeId || null,
          name: name || 'AI生成',
          typeCode: resourceType === 'video' ? 'agent_video' : 'agent_image',
          source: 'canvas_generate',
        }),
      });
      return saved?.id || null;
    } catch (e) {
      console.warn('[Canvas] 生成结果写入资源中心失败:', e);
      toast.warning('已生成，但写入资源中心失败');
      return null;
    }
  }, [episodeId, toast]);

  /**
   * 收集参考图节点的 URL（不再转 base64，避免超大 payload）
   */
  const collectRefImages = useCallback(async (refNodeIds, canvasNodes) => {
    const refs = [];
    for (const nid of refNodeIds) {
      const refNode = canvasNodes?.find(n => n.id === nid);
      const imageUrl = getNodeImageUrl(refNode?.data);
      if (imageUrl) {
        refs.push({
          name: refNode.data.name || refNode.data.prompt || '参考图',
          base64: imageUrl, // 字段名保持 base64 兼容后端，但实际传 URL
        });
      }
    }
    return refs;
  }, []);

  const completeGeneratedResource = useCallback(({
    node,
    formData,
    resultUrl,
    resourceType,
    projectId,
    onUpdate,
    taskId = null,
    provider = null,
    model = null,
    extraData = {},
  }) => {
    const generatedAt = new Date().toISOString();
    // buildPayload 会携带节点的完整旧数据；新产物不能继承上一版本的资源中心 ID
    // 或任一素材库 AssetId。后续归档/入库成功后再按 historyEntryId 定点回填。
    const resultSnapshot = clearVideoAssetBinding({
      ...formData,
      ...extraData,
      url: resultUrl,
      mediaType: resourceType,
      taskId,
      provider,
      model,
      createdAt: generatedAt,
      status: 'completed',
      resourceId: null,
      assetProvider: null,
      originalVideoUrl: resourceType === 'video'
        ? (extraData.originalVideoUrl ?? null)
        : null,
    });
    // taskId 生成的历史 id 是稳定的；资源中心异步回调用它定点回填，避免串到后来切换的版本。
    const historyEntryId = buildGenerationHistoryEntry(node?.data || {}, resultSnapshot)?.id || null;
    const completedData = {
      ...extraData,
      isGenerating: false,
      generatingStatus: 'completed',
      lastResultUrl: resultUrl,
      lastGeneratedAt: generatedAt,
      resourceId: null,
      errorMessage: null,
      activeTaskId: null,
      activeTaskKind: null,
      activeTaskProvider: null,
      activeTaskModel: null,
      activeTaskProjectId: null,
      generationStartedAt: null,
    };

    // 服务商任务完成后立即结束节点 loading。资源中心归档是附加动作，
    // 网络缓慢或归档失败都不应让已生成结果继续显示为“生成中”。
    onUpdate(node.id, (previousData) => ({
      ...previousData,
      ...completedData,
      ...createSuccessfulGenerationPatch(previousData, resultSnapshot),
    }));

    void saveGeneratedToResourceCenter({
      url: resultUrl,
      resourceType,
      name: `AI生成-${formData.prompt?.substring(0, 20) || '未命名'}`,
      projectId,
    }).then((resourceId) => {
      if (!resourceId) return;
      onUpdate(node.id, (previousData) => {
        if (!historyEntryId) {
          return previousData.lastResultUrl === resultUrl
            ? { ...previousData, resourceId }
            : previousData;
        }
        return {
          ...previousData,
          ...backfillGenerationHistoryEntry(previousData, historyEntryId, { resourceId }),
        };
      });
    }).catch((error) => {
      // saveGeneratedToResourceCenter 已处理常规网络错误；这里兜住回写异常，
      // 防止后台归档 Promise 影响生成任务的完成链路。
      console.warn('[Canvas] 资源中心归档结果回写失败:', error);
    });
  }, [saveGeneratedToResourceCenter]);

  const startImageTaskPolling = useCallback(({
    node,
    formData,
    taskId,
    projectId,
    onUpdate,
  }) => {
    const pollKey = `image:${String(node.id)}:${String(taskId)}`;
    if (pollingStopsRef.current.has(pollKey)) {
      return pollingStopsRef.current.get(pollKey);
    }

    inflightNodeIds.current.add(String(node.id));
    setIsGenerating(true);
    let lastProgressText = '';

    const stop = startTaskPolling(taskId, {
      maxTotalMs: 45 * 60 * 1000,
      onProgress: (progress) => {
        const progressText = progress?.currentStageText
          || (Number.isFinite(Number(progress?.progress)) ? `图片生成中 ${Math.round(Number(progress.progress))}%` : '图片生成中，请稍候...');
        if (progressText === lastProgressText) return;
        lastProgressText = progressText;
        onUpdate(node.id, {
          isGenerating: true,
          generatingStatus: progressText,
          errorMessage: null,
        });
      },
      onSuccess: async (result) => {
        clearPollingStop(pollKey);
        try {
          const resultUrl = extractTaskResultUrl(result);
          if (!resultUrl) throw new Error('任务完成但没有返回图片地址');
          await completeGeneratedResource({
            node,
            formData,
            resultUrl,
            resourceType: 'image',
            projectId,
            onUpdate,
            taskId,
            provider: formData.imageProvider || getCurrentImageProvider(),
            model: formData.imageModel || null,
          });
          toast.success('图片生成成功！');
        } catch (error) {
          onUpdate(node.id, {
            isGenerating: false,
            generatingStatus: 'failed',
            errorMessage: error.message || '图片结果处理失败',
            activeTaskId: null,
            activeTaskKind: null,
            generationStartedAt: null,
          });
          toast.error(`图片结果处理失败: ${error.message || '请重试'}`);
        } finally {
          releaseInflightNode(node.id);
        }
      },
      onError: (errorMessage) => {
        clearPollingStop(pollKey);
        onUpdate(node.id, {
          isGenerating: false,
          generatingStatus: 'failed',
          errorMessage: errorMessage || '图片生成失败',
          activeTaskId: null,
          activeTaskKind: null,
          generationStartedAt: null,
        });
        toast.error(errorMessage || '图片生成失败');
        releaseInflightNode(node.id);
      },
      onPollingInterrupted: (message) => {
        clearPollingStop(pollKey);
        onUpdate(node.id, {
          isGenerating: false,
          generatingStatus: 'interrupted',
          errorMessage: message || '任务查询已中断，可继续查询同一任务',
          activeTaskId: taskId,
          activeTaskKind: 'image',
          activeTaskProjectId: projectId || null,
        });
        toast.warning(message || '任务仍在后台执行，可稍后继续查询');
        releaseInflightNode(node.id);
      },
    });

    pollingStopsRef.current.set(pollKey, stop);
    return stop;
  }, [
    clearPollingStop,
    completeGeneratedResource,
    releaseInflightNode,
    toast,
  ]);

  const startVideoTaskPolling = useCallback(({
    node,
    formData,
    taskId,
    provider,
    model,
    projectId,
    onUpdate,
  }) => {
    const pollKey = `video:${String(node.id)}:${String(taskId)}`;
    if (pollingStopsRef.current.has(pollKey)) {
      return pollingStopsRef.current.get(pollKey);
    }

    inflightNodeIds.current.add(String(node.id));
    setIsGenerating(true);
    const persistedStartedAt = Date.parse(node?.data?.generationStartedAt || '');
    const startedAt = Number.isFinite(persistedStartedAt) ? persistedStartedAt : Date.now();
    let stopped = false;
    let timeoutId = null;
    let consecutiveErrors = 0;
    let lastProgressText = '';
    const usesDurablePolling = isDurableVideoPollingProvider(provider);

    const stop = () => {
      stopped = true;
      if (timeoutId) clearTimeout(timeoutId);
    };

    const finishPolling = () => {
      clearPollingStop(pollKey);
      releaseInflightNode(node.id);
    };

    const scheduleNext = () => {
      const elapsed = Date.now() - startedAt;
      const delay = usesDurablePolling
        ? 15000
        : elapsed < 30000 ? 3000 : elapsed < 120000 ? 8000 : 15000;
      timeoutId = setTimeout(poll, delay);
    };

    const interruptPolling = (message) => {
      stop();
      onUpdate(node.id, {
        isGenerating: false,
        generatingStatus: 'interrupted',
        errorMessage: message,
        activeTaskId: taskId,
        activeTaskKind: 'video',
        activeTaskProvider: provider,
        activeTaskModel: model,
        activeTaskProjectId: projectId || null,
      });
      toast.warning(message);
      finishPolling();
    };

    const poll = async () => {
      if (stopped) return;
      const maxPollingMs = usesDurablePolling ? VIDEO_POLLING_MAX_MS : 45 * 60 * 1000;
      if (Date.now() - startedAt >= maxPollingMs) {
        interruptPolling(usesDurablePolling
          ? '视频任务查询已达到120分钟，任务仍可能在后台生成，可稍后继续查询同一任务'
          : '视频仍可能在后台生成，已暂停查询，可稍后继续');
        return;
      }

      try {
        let statusUrl = `${API_HOST}/api/video/status?provider=${encodeURIComponent(provider)}&taskId=${encodeURIComponent(taskId)}`;
        if (projectId) statusUrl += `&projectId=${encodeURIComponent(projectId)}`;
        if (model) {
          statusUrl += `&model=${encodeURIComponent(model)}`;
          statusUrl += `&subModel=${encodeURIComponent(model)}`;
        }
        const statusResult = await authenticatedFetch(statusUrl, { method: 'GET' });
        consecutiveErrors = 0;

        const normalizedStatus = String(statusResult?.status || '').toLowerCase();
        if (['success', 'succeeded', 'completed'].includes(normalizedStatus)) {
          stop();
          const videoUrl = statusResult?.videoUrl || statusResult?.resultUrl || statusResult?.url;
          if (!videoUrl) {
            onUpdate(node.id, {
              isGenerating: false,
              generatingStatus: 'failed',
              errorMessage: '视频任务完成但没有返回视频地址',
              activeTaskId: null,
              activeTaskKind: null,
              generationStartedAt: null,
            });
            toast.error('视频任务完成但没有返回视频地址');
            finishPolling();
            return;
          }

          await completeGeneratedResource({
            node,
            formData,
            resultUrl: videoUrl,
            resourceType: 'video',
            projectId,
            onUpdate,
            taskId,
            provider,
            model,
            extraData: {
              originalVideoUrl: statusResult?.originalVideoUrl,
              videoProvider: provider,
            },
          });
          toast.success('视频生成成功！');
          finishPolling();
          return;
        }

        if (['failed', 'error', 'canceled', 'cancelled', 'stopped', 'not_found'].includes(normalizedStatus)) {
          stop();
          const errorMessage = statusResult?.errorMessage || statusResult?.message || '视频生成失败';
          onUpdate(node.id, {
            isGenerating: false,
            generatingStatus: 'failed',
            errorMessage,
            activeTaskId: null,
            activeTaskKind: null,
            generationStartedAt: null,
          });
          toast.error(errorMessage);
          finishPolling();
          return;
        }

        const progressText = statusResult?.currentStageText
          || (Number.isFinite(Number(statusResult?.progress)) ? `视频生成中 ${Math.round(Number(statusResult.progress))}%` : '视频生成中，请稍候...');
        if (progressText !== lastProgressText) {
          lastProgressText = progressText;
          onUpdate(node.id, {
            isGenerating: true,
            generatingStatus: progressText,
            errorMessage: null,
          });
        }
        scheduleNext();
      } catch (error) {
        consecutiveErrors += 1;
        console.error('[Canvas] 视频任务轮询失败:', error);
        if (!usesDurablePolling && consecutiveErrors >= 15) {
          interruptPolling('网络连接暂时中断，视频仍在后台生成，可继续查询同一任务');
          return;
        }
        const retryDelay = Math.min(3000 * consecutiveErrors, 30000);
        timeoutId = setTimeout(poll, usesDurablePolling ? Math.max(15000, retryDelay) : retryDelay);
      }
    };

    pollingStopsRef.current.set(pollKey, stop);
    if (usesDurablePolling) timeoutId = setTimeout(poll, 15000);
    else poll();
    return stop;
  }, [
    clearPollingStop,
    completeGeneratedResource,
    releaseInflightNode,
    toast,
  ]);

  const resumeGenerationTask = useCallback((node, onUpdate) => {
    const taskId = node?.data?.activeTaskId;
    const taskKind = node?.data?.activeTaskKind;
    if (!taskId || !taskKind || typeof onUpdate !== 'function') return false;

    const formData = { ...(node.data || {}) };
    onUpdate(node.id, {
      isGenerating: true,
      generatingStatus: '正在恢复任务查询...',
      errorMessage: null,
    });

    if (taskKind === 'image') {
      startImageTaskPolling({
        node,
        formData,
        taskId,
        projectId: node.data?.activeTaskProjectId || node.data?.projectId || localStorage.getItem('currentProjectId'),
        onUpdate,
      });
      return true;
    }

    if (taskKind === 'video') {
      startVideoTaskPolling({
        node,
        formData,
        taskId,
        provider: node.data?.activeTaskProvider || node.data?.videoProvider || 'vidu',
        model: node.data?.activeTaskModel || node.data?.videoModel || '',
        projectId: node.data?.activeTaskProjectId || node.data?.projectId || localStorage.getItem('currentProjectId'),
        onUpdate,
      });
      return true;
    }
    return false;
  }, [startImageTaskPolling, startVideoTaskPolling]);

  /**
   * 图片生成（跟随全局 provider 配置）
   */
  const generateImage = useCallback(async (node, formData, refNodeIds, canvasNodes, onUpdate) => {
    // 防重触发：该节点已有生成在飞（双击穿透/重复回调），直接忽略
    if (node.data?.isGenerating || inflightNodeIds.current.has(String(node.id))) return;
    if (!formData.prompt) { toast.warning("请输入提示词"); return; }

    inflightNodeIds.current.add(String(node.id));
    setIsGenerating(true);
    onUpdate(node.id, { ...formData, isGenerating: true });

    try {
      const refs = await collectRefImages(refNodeIds, canvasNodes);
      const hasRefs = refs.length > 0;
      const imageProvider = normalizeImageProvider(
        formData.imageProvider || getCurrentImageProvider(),
      );
      assertWanxImageInputLimit(refs, imageProvider);

      const payload = {
        prompt: formData.prompt,
        aspectRatio: formData.aspectRatio || "16:9",
        imageQualityTier: normalizeWanxImageQualityTier(
          formData.imageQualityTier || '2K',
          imageProvider,
        ),
        projectId: node.data?.projectId || localStorage.getItem('currentProjectId') || null,
        episodeId: episodeId || null,
        // 画布自由生图：只使用连线的参考节点，不注入项目级角色/剧本上下文
        skipStoryboardContext: true,
        // 普通图片节点不套用“分镜大师/生产级/无文字”等分镜业务包装。
        promptMode: 'plain',
        // 旧节点没有该字段时默认携带项目画风，用户可在节点编辑区关闭。
        includeProjectStyle: formData.includeProjectStyle !== false,
      };

      if (hasRefs) {
        payload.characterRefs = [];
        payload.sceneRefs = [];
        payload.propRefs = [];
        refs.forEach(r => payload.sceneRefs.push(r));
      }

      const task = await submitTask("TEXT_AND_REFERENCE_TO_IMAGE", payload, {
        provider: imageProvider,
        styleAnchor: currentStyle || "universal",
        projectId: payload.projectId,
        episodeId: payload.episodeId,
        source: "canvas",
      });
      const taskId = task?.taskId;
      if (!taskId) throw new Error('未获取到图片任务ID');
      onUpdate(node.id, {
        isGenerating: true,
        generatingStatus: '图片生成中，请稍候...',
        errorMessage: null,
        activeTaskId: taskId,
        activeTaskKind: 'image',
        activeTaskProjectId: payload.projectId || null,
        generationStartedAt: new Date().toISOString(),
      });
      toast.success('图片任务已提交，正在生成中...');
      startImageTaskPolling({
        node,
        formData,
        taskId,
        projectId: payload.projectId,
        onUpdate,
      });
    } catch (error) {
      console.error("生成失败:", error);
      toast.error(`生成失败: ${error.message || "请重试"}`);
      onUpdate(node.id, {
        isGenerating: false,
        generatingStatus: 'failed',
        errorMessage: error.message || '生成失败',
        activeTaskId: null,
        activeTaskKind: null,
        generationStartedAt: null,
      });
      releaseInflightNode(node.id);
    }
  }, [
    collectRefImages,
    currentStyle,
    episodeId,
    releaseInflightNode,
    startImageTaskPolling,
    toast,
  ]);

  /**
   * 视频生成
   */
  const generateVideo = useCallback(async (node, formData, refNodeIds, canvasNodes, onUpdate) => {
    // 防重触发：该节点已有生成在飞（双击穿透/重复回调），直接忽略
    if (node.data?.isGenerating || inflightNodeIds.current.has(String(node.id))) return;
    const requestedProvider = formData.videoProvider || 'vidu';
    if (!formData.prompt && !isWanxVideoProvider(requestedProvider)) {
      toast.warning("请输入提示词");
      return;
    }

    inflightNodeIds.current.add(String(node.id));
    setIsGenerating(true);
    onUpdate(node.id, { ...formData, isGenerating: true, generatingStatus: '提交中...' });

    try {
      const projectId = node.data.projectId || formData.projectId || localStorage.getItem('currentProjectId');
      const rawProvider = formData.videoProvider || 'vidu';
      let provider = rawProvider === 'kling' || rawProvider === 'klingService' ? 'vidu' : rawProvider;
      provider = normalizeSeedance25SubmissionProvider(provider) || provider;
      const isSeedance25 = isSeedance25Provider(provider);
      if (isSeedanceVideoProvider(provider) && !isDomesticOfficialSeedance(provider)) {
        const projectProvider = getProjectVideoProvider(projectId, "guavideo");
        if (!isSeedanceVideoProvider(projectProvider)) {
          throw new Error("当前项目的视频服务配置无效，请刷新项目配置后重试");
        }
        provider = projectProvider;
      }
      const model = isWanxVideoProvider(provider)
        ? normalizeWanxVideoModel(formData.videoModel)
        : provider === 'vidu' && (!formData.videoModel || String(formData.videoModel).startsWith('kling-'))
          ? 'viduq3-turbo'
          : (formData.videoModel || 'viduq3-turbo');
      const duration = String(isWanxVideoProvider(provider)
        ? normalizeWanxVideoDuration(formData.videoDuration, 5)
        : (formData.videoDuration ?? 5));
      const defaultVideoRatio = getProjectDefaultVideoRatio(projectId);
      const videoAspectRatio = formData.aspectRatio || defaultVideoRatio || '9:16';

      const payload = {
        prompt: formData.prompt,
        duration,
        provider,
        model,
        projectId,
        aspectRatio: videoAspectRatio,
        // 通用分辨率（480p/720p/1080p，默认 720p；后端不识别该字段时安全忽略）
        resolution: formData.videoResolution || '720p',
      };

      // 片3：收集连入的音频节点 URL（resourceType:'audio' 的资源节点，取 data.url，去重）
      const connectedAudioUrls = [];
      for (const nid of refNodeIds) {
        const refNode = canvasNodes?.find(n => n.id === nid);
        const d = refNode?.data || {};
        if (d.resourceType !== 'audio') continue;
        const audioUrl = (typeof d.url === 'string' && (d.url.startsWith('http') || d.url.startsWith('/') || d.url.startsWith('blob:'))) ? d.url : null;
        if (audioUrl && !connectedAudioUrls.includes(audioUrl)) connectedAudioUrls.push(audioUrl);
      }
      // 参考图/首帧取 URL 时跳过音频节点（防止音频 URL 被误作 image_url）
      const isAudioRefNode = (n) => n?.data?.resourceType === 'audio';

      // Vidu 专属参数
      if (provider === 'vidu') {
        payload.resolution = formData.videoResolution || '720p';
        payload.audio = formData.videoAudio !== false;
        if (formData.videoBgm) payload.bgm = true;
      }

      if (isSeedance25) {
        Object.assign(payload, buildCanvasSeedance25Submission({
          provider, formData, defaultRatio: defaultVideoRatio,
          referenceNodes: refNodeIds.map(id => canvasNodes?.find(candidate => candidate.id === id)).filter(Boolean),
        }));
      }

      // Seedance 2.0 线路共用 guaVideoContents，素材引用严格按项目绑定的服务读取。
      if (isSeedanceVideoProvider(provider)) {
        // Only the domestic legacy proxy reuses a stored asset ID. Overseas 2.0
        // sends the original URL so the backend can ingest into Dreamina v3.
        const applyIngestedAssetRef = (content, nodeData) => {
          if (isDomesticOfficialSeedance(provider)) return content;
          const assetMeta = getEffectiveAssetMeta(nodeData || {}, provider);
          const assetId = String(assetMeta.assetId || '').replace(/^asset:\/\//i, '');
          if (assetId && assetMeta.status === 'Active' && provider === 'guavideo') {
            content.assetId = assetId;
          }
          return content;
        };
        const guaMode = formData.guaVideoMode || 'ref2v';
        const guaContents = [];
        const guaReferenceItems = [];

        if (guaMode === 'i2v') {
          // 首帧从参考图节点取（跳过音频节点）
          if (refNodeIds.length > 0) {
            const firstRef = refNodeIds
              .map(nid => canvasNodes?.find(n => n.id === nid))
              .find(n => n && !isAudioRefNode(n));
            const startUrl = firstRef && isDreaminaProxySeedance20Provider(provider)
              ? requireDreaminaProxyOriginalUrl(firstRef.data)
              : isDomesticOfficialSeedance(provider) ? getSeedance25ReferenceUrl(firstRef?.data) : getNodeImageUrl(firstRef?.data);
            if (startUrl) guaContents.push(applyIngestedAssetRef({ type: 'image_url', url: startUrl, role: 'first_frame' }, firstRef?.data));
          }
        } else if (guaMode === 'ref2v') {
          // 参考图：从连入的参考图节点取；Seedance 不再接收参考音频。
          for (const nid of refNodeIds) {
            const refNode = canvasNodes?.find(n => n.id === nid);
            if (isDomesticOfficialSeedance(provider) && ['video', 'audio'].includes(refNode?.data?.resourceType)) {
              const type = refNode.data.resourceType;
              const url = getSeedance25ReferenceUrl(refNode.data);
              if (!url || /^asset:\/\//i.test(url)) throw new Error('国内官方参考素材缺少原始 URL，请重新选择素材');
              if (url) guaContents.push({ type: `${type}_url`, url, role: `reference_${type}` });
              continue;
            }
            if (isAudioRefNode(refNode)) continue;
            const imageUrl = isDreaminaProxySeedance20Provider(provider)
              ? getDreaminaProxyOriginalUrl(refNode?.data)
              : isDomesticOfficialSeedance(provider) ? getSeedance25ReferenceUrl(refNode?.data) : getNodeImageUrl(refNode?.data);
            if (isDreaminaProxySeedance20Provider(provider) && !imageUrl && (
              refNode?.data?.assetId || refNode?.data?.engGuaVideoAssetId || refNode?.data?.guaOminiAssetId
              || refNode?.data?.dreaminaProxyAssetId
              || /^asset:\/\//i.test(refNode?.data?.url || '')
            )) requireDreaminaProxyOriginalUrl(refNode?.data);
            if (isDomesticOfficialSeedance(provider) && !imageUrl && (refNode?.data?.resourceType === 'image' || refNode?.data?.assetId)) throw new Error('国内官方参考图片缺少原始 URL，请重新选择原图');
            if (imageUrl) {
              guaReferenceItems.push({
                ...(refNode?.data || {}),
                url: imageUrl,
                name: refNode?.data?.name || refNode?.data?.prompt || '参考图',
              });
              guaContents.push(applyIngestedAssetRef({ type: 'image_url', url: imageUrl, role: 'reference_image' }, refNode?.data));
            }
          }
          const promptWithVoice = applyGuaVoiceBindingPrompt(formData.prompt || '', [], {
            overseas: isOverseasVideoProvider(provider),
          });
          if (promptWithVoice) guaContents.unshift({ type: 'text', text: promptWithVoice });
          // 参考视频
          (formData.guaRefVideos || []).forEach(url => {
            guaContents.push({ type: 'video_url', url, role: 'reference_video' });
          });
        } else if (formData.prompt) {
          guaContents.push({ type: 'text', text: formData.prompt });
        }
        // t2v: 只有 text
        if (guaMode === 'i2v' && formData.prompt) {
          guaContents.unshift({ type: 'text', text: formData.prompt });
        }

        const preparedGuaContents = await preflightVideoAssetContents({
          provider,
          projectId,
          contents: guaContents,
          context: {
            episodeId,
            name: node.data?.name || '画布节点参考图',
            source: 'canvas_generate',
            sourceDetail: `canvas_node_${node.id}`,
            typeCode: 'agent_image',
          },
          shouldContinue: () => inflightNodeIds.current.has(String(node.id)),
        });
        payload.prompt = preparedGuaContents.find((item) => item.type === 'text')?.text || payload.prompt;
        payload.guaVideoContents = preparedGuaContents;
        payload.guaVideoMode = guaMode;
        payload.generateAudio = formData.guaAudio ?? true;
        payload.ratio = formData.guaRatio || formData.aspectRatio || defaultVideoRatio || '9:16';
        // 通用 resolution 同步映射到 GuaVideo 线路的 guaResolution（保留旧字段 guaResolution 的兼容回退）
        const submittedResolution = normalizeVideoResolutionForProvider(
          provider,
          formData.videoResolution || formData.guaResolution,
          '720p',
        );
        payload.resolution = submittedResolution;
        payload.guaResolution = submittedResolution;
        if (guaMode === 't2v' && formData.guaWebSearch) payload.guaWebSearch = true;
        payload.projectId = projectId;
        // ref2v/t2v 不需要 startImageUrl
        if (guaMode !== 'i2v') {
          payload.startImageUrl = null;
        }
      }

      // 万相 3.0 使用独立 typed media 协议，绝不复用 Seedance 素材库字段。
      if (isWanxVideoProvider(provider)) {
        const wanxMode = formData.wanxMode || 'img2video';
        const connectedMedia = [];
        for (const nid of refNodeIds) {
          const refNode = canvasNodes?.find(candidate => candidate.id === nid);
          if (!refNode) continue;
          const d = refNode.data || {};
          const outputType = String(getNodeOutputType(refNode) || '').toLowerCase();
          const typeHint = `${refNode.type || ''} ${d.resourceType || ''} ${d.mediaType || ''} ${d.type || ''} ${d.legacyType || ''}`.toLowerCase();
          const durationSeconds = Number(d.duration ?? d.videoDuration ?? d.audioDuration);

          if (outputType === 'audio' || typeHint.includes('audio')) {
            const url = d.url || d.audioUrl || d.sourceUrl || d.lastResultUrl;
            if (url) {
              connectedMedia.push({
                type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_AUDIO,
                url,
                ...(Number.isFinite(durationSeconds) && durationSeconds > 0 ? { duration: durationSeconds } : {}),
              });
            }
            continue;
          }

          if (outputType === 'video' || typeHint.includes('video') || typeHint.includes('motionstoryboard')) {
            const url = d.exportedVideoUrl || d.videoUrl || d.url || d.sourceUrl || d.lastResultUrl;
            if (url) {
              connectedMedia.push({
                type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_VIDEO,
                url,
                ...(Number.isFinite(durationSeconds) && durationSeconds > 0 ? { duration: durationSeconds } : {}),
              });
            }
            continue;
          }

          const imageUrl = getNodeImageUrl(d);
          if (imageUrl) connectedMedia.push({ type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE, url: imageUrl });
        }

        const storedMedia = normalizeWanxVideoMedia(formData.wanxMedia);
        const dedupeByTypeAndUrl = (items) => Array.from(
          new Map(items.filter(item => item?.url).map(item => [`${item.type}:${item.url}`, item])).values(),
        );
        let wanxMedia = [];

        if (wanxMode === 'img2video' || wanxMode === 'start-end') {
          const frameCandidates = dedupeByTypeAndUrl([
            ...storedMedia
              .filter(item => [
                WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME,
                WANX_VIDEO_MEDIA_TYPES.LAST_FRAME,
                WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE,
              ].includes(item.type))
              .map(item => ({ ...item, type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE })),
            ...connectedMedia.filter(item => item.type === WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE),
          ]);
          if (frameCandidates[0]) {
            wanxMedia.push({ ...frameCandidates[0], type: WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME });
          }
          if (wanxMode === 'start-end' && frameCandidates[1]) {
            wanxMedia.push({ ...frameCandidates[1], type: WANX_VIDEO_MEDIA_TYPES.LAST_FRAME });
          }
        } else if (wanxMode === 'reference') {
          const asReference = (item) => {
            if ([WANX_VIDEO_MEDIA_TYPES.FIRST_FRAME, WANX_VIDEO_MEDIA_TYPES.LAST_FRAME].includes(item.type)) {
              return { ...item, type: WANX_VIDEO_MEDIA_TYPES.REFERENCE_IMAGE };
            }
            return item;
          };
          wanxMedia = dedupeByTypeAndUrl([...storedMedia, ...connectedMedia].map(asReference));
        }

        const submittedDuration = normalizeWanxVideoDuration(formData.videoDuration, 5);
        wanxMedia = assertWanxVideoMediaSelection(wanxMedia, wanxMode, submittedDuration);
        if (!String(formData.prompt || '').trim() && wanxMedia.length === 0) {
          throw new Error('提示词和媒体素材至少填写一项');
        }
        payload.model = model;
        payload.duration = submittedDuration;
        payload.resolution = normalizeWanxVideoResolution(formData.videoResolution);
        payload.ratio = normalizeWanxVideoRatio(formData.aspectRatio);
        payload.aspectRatio = payload.ratio;
        payload.audio = formData.wanxAudio !== false;
        payload.sound = payload.audio ? 'on' : 'off';
        payload.watermark = formData.wanxWatermark === true;
        payload.promptExtend = formData.wanxPromptExtend !== false;
        const wanxSeed = normalizeWanxVideoSeed(formData.wanxSeed);
        if (wanxSeed != null) payload.seed = wanxSeed;
        payload.mode = wanxMode;
        payload.generationMode = wanxMode;
        payload.wanxMedia = wanxMedia.map((item) => ({
          type: item.type,
          url: item.url,
          ...(item.duration != null ? { durationSeconds: item.duration } : {}),
        }));
        payload.startImageUrl = null;
        payload.endImageUrl = null;
        payload.audioUrl = null;
        payload.referenceUrls = [];
      }

      // 首帧图片（非 GuaVideo 线路时从参考图节点取第一张，跳过音频节点）
      if (!isSeedance25 && !isSeedanceVideoProvider(provider) && !isWanxVideoProvider(provider) && refNodeIds.length > 0) {
        const firstRef = refNodeIds
          .map(nid => canvasNodes?.find(n => n.id === nid))
          .find(n => n && !isAudioRefNode(n));
        const startUrl = getNodeImageUrl(firstRef?.data);
        if (startUrl) payload.startImageUrl = startUrl;
      }

      // 片3：通用线路参考音频 — 第一个连入音频写入 audioUrl（后端 VideoTaskRequest 既有字段，安全附加；
      // guavideo/engguavideo 线路走 guaVideoContents，不写该字段）
      if (!isSeedance25 && !isSeedanceVideoProvider(provider) && !isWanxVideoProvider(provider) && connectedAudioUrls.length > 0 && !payload.audioUrl) {
        payload.audioUrl = connectedAudioUrls[0];
      }

      // 动态分镜参考视频（motionStoryboard 节点的导出视频）
      const motionNodes = refNodeIds
        .map(nid => canvasNodes?.find(n => n.id === nid))
        .filter(n => n?.type === 'motionStoryboard' && n.data?.exportedVideoUrl);

      if (motionNodes.length > 0) {
        if (isSeedanceVideoProvider(provider)) {
          motionNodes.forEach(n => {
            if (!payload.guaVideoContents) payload.guaVideoContents = [];
            payload.guaVideoContents.push({
              type: 'video_url',
              url: n.data.exportedVideoUrl,
              role: 'reference_video',
            });
          });
        }
      }

      // 历史记录保存“实际提交值”，不能把 Omini 被降级前的 1080p/4K 或旧项目线路写回历史。
      const submittedFormData = {
        ...formData,
        videoProvider: provider,
        videoModel: payload.model,
        videoDuration: isWanxVideoProvider(provider) || isSeedance25
          ? payload.duration
          : (Number(duration) || formData.videoDuration || 5),
        aspectRatio: payload.ratio || payload.aspectRatio || videoAspectRatio,
        videoResolution: payload.resolution || formData.videoResolution || '720p',
        ...(isSeedance25 ? { seedance25Mode: payload.generationMode } : {}),
        ...(payload.guaVideoMode ? { guaVideoMode: payload.guaVideoMode } : {}),
        ...(payload.guaResolution ? { guaResolution: payload.guaResolution } : {}),
        ...(isWanxVideoProvider(provider) ? {
          wanxMode: payload.generationMode,
          wanxAudio: payload.audio,
          wanxWatermark: payload.watermark,
          wanxPromptExtend: payload.promptExtend,
          wanxSeed: payload.seed ?? '',
          wanxMedia: payload.wanxMedia,
        } : {}),
      };

      const usesUnifiedSubmission = isWanxVideoProvider(provider) || isSeedance25 || isDomesticOfficialSeedance(provider);
      const response = usesUnifiedSubmission
        ? await submitVideoTask({
          ...payload,
          episodeId: episodeId ? Number(episodeId) : undefined,
          clientSubmissionScope: `canvas-node:${node.id}`,
        })
        : await authenticatedFetch(`${API_HOST}/api/video/generate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

      if (isSeedanceVideoProvider(provider)) {
        const rawResponseProvider = String(response?.provider || response?.videoProvider || '').trim();
        if (rawResponseProvider && normalizeVideoProvider(rawResponseProvider) !== provider) {
          throw new Error("视频服务返回与当前项目不匹配，已拒绝接收任务");
        }
      }
      if (isWanxVideoProvider(provider)) {
        const rawResponseProvider = String(response?.provider || response?.videoProvider || '').trim();
        if (rawResponseProvider && !isWanxVideoProvider(rawResponseProvider)) {
          throw new Error("视频服务返回的厂商与万相 3.0 不匹配，已拒绝接收任务");
        }
      }

      const taskId = usesUnifiedSubmission
        ? response.taskId
        : (response.task_id || response.taskId || response.id);
      if (!taskId) throw new Error("未获取到任务ID");

      onUpdate(node.id, {
        isGenerating: true,
        generatingStatus: '视频生成中，请稍候...',
        errorMessage: null,
        activeTaskId: taskId,
        activeTaskKind: 'video',
        activeTaskProvider: provider,
        activeTaskModel: submittedFormData.videoModel,
        activeTaskProjectId: projectId || null,
        generationStartedAt: new Date().toISOString(),
        videoProvider: submittedFormData.videoProvider,
        videoModel: submittedFormData.videoModel,
        videoDuration: submittedFormData.videoDuration,
        aspectRatio: submittedFormData.aspectRatio,
        videoResolution: submittedFormData.videoResolution,
        ...(submittedFormData.guaResolution
          ? { guaResolution: submittedFormData.guaResolution }
          : {}),
        ...(isWanxVideoProvider(provider) ? {
          wanxMode: submittedFormData.wanxMode,
          wanxAudio: submittedFormData.wanxAudio,
          wanxWatermark: submittedFormData.wanxWatermark,
          wanxPromptExtend: submittedFormData.wanxPromptExtend,
          wanxSeed: submittedFormData.wanxSeed,
          wanxMedia: submittedFormData.wanxMedia,
        } : {}),
      });

      toast.success("视频任务已提交，正在生成中...");
      startVideoTaskPolling({
        node,
        formData: submittedFormData,
        taskId,
        provider,
        model: submittedFormData.videoModel,
        projectId,
        onUpdate,
      });

    } catch (error) {
      console.error("生成失败:", error);
      toast.error(`生成失败: ${error.message || "请重试"}`);
      onUpdate(node.id, {
        isGenerating: false,
        generatingStatus: 'failed',
        errorMessage: error.message || '生成失败',
        activeTaskId: null,
        activeTaskKind: null,
        generationStartedAt: null,
      });
      releaseInflightNode(node.id);
    }
  }, [episodeId, releaseInflightNode, startVideoTaskPolling, toast]);

  return { isGenerating, generateImage, generateVideo, resumeGenerationTask };
};

export default useCanvasGeneration;
